import { Alert, Button, Card, CardContent, Chip, Stack, TextField, Typography } from '@mui/material';
import { useState } from 'react';
import { executeShellCommandJson, type ShellResponse } from '../../api/shell';
import { ResultTable } from '../../components/ResultTable';
import { sanitizeShellArg } from '../../utils/shell';

export function AssetInspector() {
  const [assetJobId, setAssetJobId] = useState('');
  const [caseId, setCaseId] = useState('');
  const [getResult, setGetResult] = useState<ShellResponse | null>(null);
  const [listResult, setListResult] = useState<ShellResponse | null>(null);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  async function handleGetAsset() {
    if (!assetJobId.trim()) return;
    setLoading(true);
    setError('');
    try {
      const res = await executeShellCommandJson(`asset.get ${sanitizeShellArg(assetJobId)}`);
      setGetResult(res);
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : 'Request failed');
    } finally {
      setLoading(false);
    }
  }

  async function handleListAssets() {
    if (!caseId.trim()) return;
    setLoading(true);
    setError('');
    try {
      const res = await executeShellCommandJson(`asset.list ${sanitizeShellArg(caseId)}`);
      setListResult(res);
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : 'Request failed');
    } finally {
      setLoading(false);
    }
  }

  async function handleRetry() {
    if (!assetJobId.trim()) return;
    if (!confirm(`Retry failed asset job ${assetJobId}? This resets it to PENDING.`)) return;
    setLoading(true);
    setError('');
    try {
      const res = await executeShellCommandJson(`asset.retry ${sanitizeShellArg(assetJobId)} --write`);
      setGetResult(res);
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : 'Request failed');
    } finally {
      setLoading(false);
    }
  }

  return (
    <Stack spacing={3}>
      <Card sx={{ bgcolor: '#1a1d22' }}>
        <CardContent>
          <Stack spacing={2}>
            <Typography variant="h6">Inspect Asset Job</Typography>
            <Stack direction="row" spacing={2}>
              <TextField
                label="Asset Job ID"
                value={assetJobId}
                onChange={(e) => setAssetJobId(e.target.value)}
                fullWidth
                size="small"
                placeholder="UUID"
              />
              <Button variant="contained" onClick={handleGetAsset} disabled={loading}>
                Get
              </Button>
            </Stack>
            {getResult && <ResultTable data={getResult} />}
          </Stack>
        </CardContent>
      </Card>

      <Card sx={{ bgcolor: '#1a1d22' }}>
        <CardContent>
          <Stack spacing={2}>
            <Typography variant="h6">List Asset Jobs by Case</Typography>
            <Stack direction="row" spacing={2}>
              <TextField
                label="Case ID"
                value={caseId}
                onChange={(e) => setCaseId(e.target.value)}
                fullWidth
                size="small"
                placeholder="UUID"
              />
              <Button variant="contained" onClick={handleListAssets} disabled={loading}>
                List
              </Button>
            </Stack>
            {listResult && <ResultTable data={listResult} />}
          </Stack>
        </CardContent>
      </Card>

      <Card sx={{ bgcolor: '#1a1d22' }}>
        <CardContent>
          <Stack spacing={2}>
            <Typography variant="h6">Asset Write Actions</Typography>
            <Typography variant="body2" color="text.secondary">
              Requires <Chip label="shell.write" size="small" /> permission. Resets FAILED jobs to PENDING. Audited.
            </Typography>
            <Button
              variant="outlined"
              color="warning"
              onClick={handleRetry}
              disabled={loading || !assetJobId.trim()}
            >
              Retry Failed Job
            </Button>
          </Stack>
        </CardContent>
      </Card>

      {error && <Alert severity="error">{error}</Alert>}
    </Stack>
  );
}
